/**
 * useWebAuthn — thin React hook around @simplewebauthn/browser
 * for passkey enrolment and sign-in.
 *
 * Endpoints assumed (all under REACT_APP_BACKEND_URL):
 *   POST /api/webauthn/register-begin       (auth required)
 *   POST /api/webauthn/register-complete    (auth required)
 *   POST /api/webauthn/login-begin
 *   POST /api/webauthn/login-complete
 *   GET  /api/webauthn/has-passkey?email=
 *   GET  /api/webauthn/credentials          (auth required)
 */
import { useCallback, useState } from 'react';
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
import { authFetch } from './authFetch';
import { safeStorage, userTimerSlice } from './safeStorage';

const API = process.env.REACT_APP_BACKEND_URL;

export function isWebAuthnSupported() {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

export async function fetchHasPasskey(email) {
  if (!email) return false;
  try {
    const res = await fetch(
      `${API}/api/webauthn/has-passkey?email=${encodeURIComponent(email)}`,
      { credentials: 'include' }
    );
    if (!res.ok) return false;
    const data = await res.json();
    return Boolean(data.has_passkey);
  } catch {
    return false;
  }
}

export function useWebAuthn() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  /** Enrol a new passkey for the currently authenticated user. */
  const enrolPasskey = useCallback(async ({ label } = {}) => {
    setBusy(true);
    setError(null);
    try {
      const beginRes = await authFetch(`${API}/api/webauthn/register-begin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: label || null }),
      });
      if (!beginRes.ok) {
        const t = await beginRes.text();
        throw new Error(t || 'Could not start passkey enrolment');
      }
      const options = await beginRes.json();

      // Browser prompt: Face ID / Touch ID / device PIN
      const credential = await startRegistration({ optionsJSON: options });

      const completeRes = await authFetch(`${API}/api/webauthn/register-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential, label: label || null }),
      });
      if (!completeRes.ok) {
        const detail = await completeRes.json().catch(() => ({}));
        throw new Error(detail.detail || 'Passkey enrolment failed');
      }
      return await completeRes.json();
    } catch (e) {
      // Translate the most user-hostile errors
      const msg = friendlyError(e);
      setError(msg);
      throw new Error(msg);
    } finally {
      setBusy(false);
    }
  }, []);

  /**
   * Sign in with a passkey. Returns the user object on success and persists
   * the JWT via safeStorage (matches the password flow).
   */
  const signInWithPasskey = useCallback(async (email) => {
    setBusy(true);
    setError(null);
    try {
      if (!email) throw new Error('Enter your email first');

      const beginRes = await fetch(`${API}/api/webauthn/login-begin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      if (!beginRes.ok) {
        throw new Error('Could not start passkey sign-in');
      }
      const options = await beginRes.json();

      const credential = await startAuthentication({ optionsJSON: options });

      const completeRes = await fetch(`${API}/api/webauthn/login-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, credential }),
      });
      if (!completeRes.ok) {
        const detail = await completeRes.json().catch(() => ({}));
        throw new Error(detail.detail || 'Passkey sign-in failed');
      }
      const data = await completeRes.json();

      if (data.token) {
        safeStorage.setItem('token', data.token);
        safeStorage.setItem('user', JSON.stringify(userTimerSlice(data.user)));
        safeStorage.setItem('invite_verified', 'true');
      }
      return data;
    } catch (e) {
      const msg = friendlyError(e);
      setError(msg);
      throw new Error(msg);
    } finally {
      setBusy(false);
    }
  }, []);

  return { busy, error, setError, enrolPasskey, signInWithPasskey };
}

function friendlyError(e) {
  const name = e?.name || '';
  const msg = String(e?.message || e);
  if (name === 'NotAllowedError' || /not allowed/i.test(msg)) {
    return 'Cancelled — please try again.';
  }
  if (name === 'InvalidStateError' || /already registered/i.test(msg)) {
    return 'This device already has a passkey for this account.';
  }
  if (name === 'NotSupportedError' || /not supported/i.test(msg)) {
    return 'Your browser or device does not support passkeys.';
  }
  if (/no active challenge/i.test(msg)) {
    return 'Session expired — please retry.';
  }
  return msg;
}

/* localStorage keys for one-time prompt suppression. */
export const PASSKEY_PROMPT_DISMISSED_KEY = 'axiom_passkey_prompt_dismissed_v1';
